import React, { useState } from 'react';

interface SimpleDragProps {
  useDatabase?: boolean;
  hasSupabaseConfig?: boolean;
  isLoading?: boolean;
  hasErrors?: boolean;
  playersError?: string | null;
  paymentsError?: string | null;
  expensesError?: string | null;
  configError?: string | null;
  onSyncData?: () => void;
}

export const SimpleDrag: React.FC<SimpleDragProps> = ({
  useDatabase = false,
  hasSupabaseConfig = false,
  isLoading = false,
  hasErrors = false,
  playersError,
  paymentsError,
  expensesError,
  configError,
  onSyncData
}) => {
  const [position, setPosition] = useState({ x: window.innerWidth - 80, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleDrag = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    e.preventDefault();

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newX = Math.max(0, Math.min(moveEvent.clientX - dragStart.x, window.innerWidth - (isExpanded ? 400 : 60)));
      const newY = Math.max(0, Math.min(moveEvent.clientY - dragStart.y, window.innerHeight - (isExpanded ? 300 : 60)));

      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Dynamic status calculation
  const getStatusColor = () => {
    if (isLoading) return '#fbbf24'; // yellow
    if (!useDatabase) return '#10b981'; // green for localStorage
    if (hasErrors) return '#ef4444'; // red
    return '#10b981'; // green
  };

  const getStatusText = () => {
    if (isLoading) return 'Connecting...';
    if (!useDatabase) return 'localStorage';
    if (hasErrors) return 'Error';
    return 'Connected';
  };

  const getModeText = () => {
    if (useDatabase) {
      return hasSupabaseConfig ? 'Production (Supabase)' : 'Database Mode (No Config)';
    }
    return 'Development (localStorage)';
  };

  return (
    <div
      style={{
        position: 'fixed',
        left: position.x + 'px',
        top: position.y + 'px',
        zIndex: 9999,
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none'
      }}
      onMouseDown={handleDrag}
    >
      {/* Collapsed Button */}
      {!isExpanded && (
        <button
          onClick={handleToggle}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            width: '60px',
            height: '60px',
            backgroundColor: getStatusColor(),
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            border: '3px solid white',
            boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
            cursor: 'pointer'
          }}
        >
          DB
        </button>
      )}

      {/* Expanded Modal */}
      {isExpanded && (
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid #e5e7eb',
            padding: '24px',
            minWidth: '384px',
            maxWidth: '448px'
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontWeight: 'bold', fontSize: '18px', color: '#374151', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: getStatusColor() }}></div>
              Database Status
              <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '8px' }}>📍 Draggable</span>
            </h3>
            <button
              onClick={handleToggle}
              style={{
                color: '#9ca3af',
                fontSize: '20px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              ✕
            </button>
          </div>

          {/* Status Content */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>ENVIRONMENT</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
                <span style={{ color: '#6b7280' }}>Mode:</span>
                <span style={{
                  fontWeight: '500',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151'
                }}>
                  {getModeText()}
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280', fontWeight: '500' }}>Database:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getStatusColor() }}></div>
              <span style={{ fontWeight: '500', color: getStatusColor() }}>{getStatusText()}</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ color: '#6b7280', fontWeight: '500' }}>Data Sync:</span>
            <span style={{ fontWeight: '500', color: hasErrors ? '#ef4444' : getStatusColor() }}>
              {hasErrors ? 'Error' : isLoading ? 'Syncing...' : 'In Sync'}
            </span>
          </div>

          {/* Info/Error Notes */}
          {hasErrors && (
            <div style={{
              marginTop: '8px',
              padding: '8px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              fontSize: '12px'
            }}>
              <div style={{ color: '#dc2626', marginBottom: '4px' }}>⚠️ Errors:</div>
              <div style={{ color: '#dc2626' }}>
                {playersError && <div>• Players: {playersError}</div>}
                {paymentsError && <div>• Payments: {paymentsError}</div>}
                {expensesError && <div>• Expenses: {expensesError}</div>}
                {configError && <div>• Config: {configError}</div>}
              </div>
            </div>
          )}

          {!hasErrors && (
            <div style={{
              marginTop: '8px',
              padding: '8px',
              backgroundColor: useDatabase ? '#eff6ff' : '#f0fdf4',
              border: `1px solid ${useDatabase ? '#bfdbfe' : '#bbf7d0'}`,
              borderRadius: '6px',
              fontSize: '12px'
            }}>
              <div style={{ color: useDatabase ? '#1e40af' : '#166534', marginBottom: '4px' }}>💡 Info:</div>
              <div style={{ color: useDatabase ? '#1d4ed8' : '#166534' }}>
                {useDatabase
                  ? hasSupabaseConfig
                    ? 'Connected to Supabase database'
                    : 'Database mode enabled but missing environment variables'
                  : 'Running in localStorage mode'
                }
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
            <button
              onClick={() => onSyncData && onSyncData()}
              disabled={isLoading}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: '12px',
                fontWeight: '500',
                backgroundColor: isLoading ? '#9ca3af' : hasErrors ? '#ef4444' : '#3b82f6',
                color: 'white',
                borderRadius: '6px',
                border: 'none',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = hasErrors ? '#dc2626' : '#2563eb';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = hasErrors ? '#ef4444' : '#3b82f6';
                }
              }}
            >
              {isLoading ? 'Loading...' : hasErrors ? 'Retry' : useDatabase ? 'Refresh Data' : 'No Action'}
            </button>

            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 12px',
                fontSize: '12px',
                fontWeight: '500',
                backgroundColor: '#6b7280',
                color: 'white',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b7280'}
              title="Reload App"
            >
              🔄
            </button>
          </div>
        </div>
      )}
    </div>
  );
};